import { dobForDisplay } from "./chatSearch";

const PATIENT_ACCOUNT_SOURCE = "patient-account";
const CLINICAL_PROFILE_SOURCE = "clinical-profile";
const PATIENT_ID_MAX_LENGTH = 256;
const hasOwn = (source, field) => Object.prototype.hasOwnProperty.call(source || {}, field);
const hasIdentityControlCharacter = (value) =>
  [...value].some((character) => {
    const codePoint = character.codePointAt(0);
    return (
      codePoint <= 0x1f ||
      (codePoint >= 0x7f && codePoint <= 0x9f) ||
      codePoint === 0x61c ||
      (codePoint >= 0x200b && codePoint <= 0x200f) ||
      (codePoint >= 0x202a && codePoint <= 0x202e) ||
      codePoint === 0x2060 ||
      (codePoint >= 0x2066 && codePoint <= 0x2069) ||
      codePoint === 0xfeff
    );
  });
const validPatientIdentityId = (value) =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= PATIENT_ID_MAX_LENGTH &&
  value === value.trim() &&
  !hasIdentityControlCharacter(value);

// Producer provenance is supplied only by these source-specific entry points.
// An exact marker for the other producer is a conflict, so fail closed rather
// than relabeling its generic display id as a typed identity.
const normalizeSelectablePatient = (patient, source) => {
  if (!patient || typeof patient !== "object" || Array.isArray(patient)) return null;
  const conflictingSource = source === PATIENT_ACCOUNT_SOURCE ? CLINICAL_PROFILE_SOURCE : PATIENT_ACCOUNT_SOURCE;
  if (patient.source === conflictingSource) return null;
  // `id` is what every consumer routes and fetches on (/patients/:id, getPatient,
  // getClinicalProfile). A row without one renders fine and then navigates to
  // /patients/undefined, so drop it here rather than let the gap reach a URL.
  if (!validPatientIdentityId(patient.id)) return null;

  const isClinicalProfile = source === CLINICAL_PROFILE_SOURCE;
  const alreadyNormalizedForSource = patient.source === source;
  const primaryIdentityField = isClinicalProfile ? "clinicalProfileId" : "patientUserId";
  if (alreadyNormalizedForSource && hasOwn(patient, primaryIdentityField) && patient[primaryIdentityField] !== patient.id) {
    return null;
  }

  return {
    id: patient.id,
    source,
    patientUserId: isClinicalProfile ? (patient.patientUserId ?? patient.userId ?? null) : patient.id,
    clinicalProfileId: isClinicalProfile ? patient.id : (patient.clinicalProfileId ?? null),
    firstName: patient.firstName || "",
    lastName: patient.lastName || "",
    email: patient.email || "",
    phone: patient.mobilePhone || patient.phone || "",
    dob: dobForDisplay(patient.dob),
    address: patient.address || "",
    city: patient.city || "",
    state: patient.state || "",
    zip: patient.zip || "",
    raw: patient.raw || patient,
  };
};

export const normalizePatientAccountRow = (patient) => normalizeSelectablePatient(patient, PATIENT_ACCOUNT_SOURCE);

export const normalizeClinicalProfileRow = (patient) => normalizeSelectablePatient(patient, CLINICAL_PROFILE_SOURCE);

// Extracts only explicit typed identity from a normalized directory row.
// Generic ids, raw records, and demographics never participate.
export const frozenPatientIdentityForRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  if (row.source !== PATIENT_ACCOUNT_SOURCE && row.source !== CLINICAL_PROFILE_SOURCE) return null;
  if (!hasOwn(row, "patientUserId") || !hasOwn(row, "clinicalProfileId")) return null;

  const { patientUserId, clinicalProfileId } = row;
  if (patientUserId !== null && !validPatientIdentityId(patientUserId)) return null;
  if (clinicalProfileId !== null && !validPatientIdentityId(clinicalProfileId)) return null;
  if (row.source === PATIENT_ACCOUNT_SOURCE && !validPatientIdentityId(patientUserId)) return null;
  if (row.source === CLINICAL_PROFILE_SOURCE && !validPatientIdentityId(clinicalProfileId)) return null;

  return Object.freeze({
    source: row.source,
    patientUserId,
    clinicalProfileId,
  });
};
